import React, { memo, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner, FaCircleExclamation } from 'react-icons/fa6';
import ConfirmDialog from '@/shared/components/modals/ConfirmDialog';
import { Modal } from '@/shared/components/modals';
import { TrainingWorkflowButtonsProps } from './types';
import { ValidationChecklist } from './ValidationChecklist';

/**
 * TrainingWorkflowButtons component
 * Renders workflow action buttons based on current workflow step
 */
export const TrainingWorkflowButtons: React.FC<TrainingWorkflowButtonsProps> = memo(({
  workflowStep,
  uploadSessionId,
  isUploading,
  isGeneratingDatasets,
  isDataValid = true,
  validationErrors = [],
  zeitschritteStatus,
  inputFilesStatus,
  outputFilesStatus,
  duplicateBezeichnungStatus,
  timeInfoStatus,
  onGenerateDatasets
}) => {
  const { t } = useTranslation('training');
  const validationTitleId = useId();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  // Determine if generate button should be disabled
  const isGenerateDisabled = !uploadSessionId || isUploading || isGeneratingDatasets || !isDataValid;

  const handleGenerateDatasetsClick = () => {
    if (!isDataValid) {
      setShowValidationErrors(true);
      return;
    }
    setShowConfirmDialog(true);
  };

  const handleConfirmGenerate = () => {
    setShowConfirmDialog(false);
    onGenerateDatasets();
  };

  const handleCancelGenerate = () => {
    setShowConfirmDialog(false);
  };

  return (
    <div className="mt-4 sm:mt-6 space-y-3">
      {/* Validation Checklist - only show during upload phase */}
      {workflowStep === 'upload' && zeitschritteStatus && inputFilesStatus && outputFilesStatus && (
        <ValidationChecklist
          zeitschritteStatus={zeitschritteStatus}
          inputFilesStatus={inputFilesStatus}
          outputFilesStatus={outputFilesStatus}
          duplicateBezeichnungStatus={duplicateBezeichnungStatus}
          timeInfoStatus={timeInfoStatus}
        />
      )}

      {/* Main action buttons */}
      <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
        {/* Generate Datasets Button */}
        {workflowStep === 'upload' && (
          <button
            type="button"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
              isGenerateDisabled
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700 active:scale-[0.98]'
            }`}
            onClick={handleGenerateDatasetsClick}
            disabled={isGenerateDisabled}
            title={!isDataValid ? t('training.buttons.validationRequired', 'Please fill all required fields') : undefined}
          >
            {isGeneratingDatasets ? (
              <>
                <FaSpinner className="animate-spin text-xs" />
                <span>{t('training.buttons.generatingDatasets')}</span>
              </>
            ) : (
              <>
                {!isDataValid && <FaCircleExclamation className="text-amber-300 text-xs" />}
                <span>{t('training.buttons.generateDatasetsAndPlots')}</span>
              </>
            )}
          </button>
        )}

        {/* Processing Indicator for phase1 (dataset gen) and phase3 (model training) */}
        {(workflowStep === 'phase1' || workflowStep === 'phase3') && (
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-accent text-primary cursor-not-allowed"
            disabled
          >
            <FaSpinner className="animate-spin text-xs" />
            <span>
              {workflowStep === 'phase1'
                ? t('training.buttons.generatingDatasets')
                : t('training.buttons.trainingModels')
              }
            </span>
          </button>
        )}
      </div>

      {/* Confirmation Dialog for Generate Datasets (W11-C T1: shared ConfirmDialog) */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        title={t('training.confirmRegenerateDatasets.title')}
        message={t('training.confirmRegenerateDatasets.message')}
        suggestion={t('training.confirmRegenerateDatasets.suggestion')}
        confirmLabel={t('training.confirmRegenerateDatasets.confirm')}
        cancelLabel={t('training.confirmRegenerateDatasets.cancel')}
        destructive={true}
        onConfirm={handleConfirmGenerate}
        onCancel={handleCancelGenerate}
      />

      {/* Validation Errors Dialog (FIX-9: shared Modal wrapper) */}
      <Modal
        isOpen={showValidationErrors && validationErrors.length > 0}
        onClose={() => setShowValidationErrors(false)}
        titleId={validationTitleId}
        size="lg"
        className="ring-1 ring-border/60 rounded-xl shadow-2xl"
      >
        {/* Header */}
        <div className="px-5 py-4 bg-gradient-to-r from-red-50 to-rose-50/50 border-b border-red-200/60">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <FaCircleExclamation className="text-red-600 text-[10px]" />
            </div>
            <h3 id={validationTitleId} className="text-base font-semibold text-foreground tracking-tight">
              {t('training.validation.title', 'Required Fields Missing')}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 max-h-80 overflow-y-auto">
          <p className="text-xs text-muted-foreground mb-3">
            {t('training.validation.description', 'Please fill in all required fields before generating datasets:')}
          </p>
          <div className="space-y-2">
            {/* Group errors by component */}
            {validationErrors.some(e => e.component === 'zeitschritte') && (
              <div className="rounded-lg border border-border bg-muted/80 p-3">
                <h4 className="text-xs font-medium text-foreground mb-1.5">
                  {t('training.validation.zeitschritte', 'General Data (Zeitschritte)')}
                </h4>
                <ul className="space-y-1">
                  {validationErrors
                    .filter(e => e.component === 'zeitschritte')
                    .map((error, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[10px] text-red-600">
                        <span className="w-1 h-1 rounded-full bg-red-400 mt-1 flex-shrink-0" />
                        {error.message}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {validationErrors.some(e => e.component === 'inputFiles') && (
              <div className="rounded-lg border border-primary/20 bg-accent/60 p-3">
                <h4 className="text-xs font-medium text-foreground mb-1.5">
                  {t('training.validation.inputFiles', 'Input Files')}
                </h4>
                <ul className="space-y-1">
                  {validationErrors
                    .filter(e => e.component === 'inputFiles')
                    .map((error, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[10px] text-red-600">
                        <span className="w-1 h-1 rounded-full bg-red-400 mt-1 flex-shrink-0" />
                        {error.message}
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {validationErrors.some(e => e.component === 'outputFiles') && (
              <div className="rounded-lg border border-emerald-200/60 bg-emerald-50/60 p-3">
                <h4 className="text-xs font-medium text-foreground mb-1.5">
                  {t('training.validation.outputFiles', 'Output Files')}
                </h4>
                <ul className="space-y-1">
                  {validationErrors
                    .filter(e => e.component === 'outputFiles')
                    .map((error, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-[10px] text-red-600">
                        <span className="w-1 h-1 rounded-full bg-red-400 mt-1 flex-shrink-0" />
                        {error.message}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t border-border bg-muted/50 flex justify-end">
          <button
            type="button"
            onClick={() => setShowValidationErrors(false)}
            className="px-4 py-1.5 text-xs font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all duration-150"
          >
            {t('common.ok', 'OK')}
          </button>
        </div>
      </Modal>
    </div>
  );
});

TrainingWorkflowButtons.displayName = 'TrainingWorkflowButtons';

export default TrainingWorkflowButtons;
